import { useState } from 'react';
import { generateId } from '../lib/utils.js';

const emptyForm = {
  nome: '', limiteAlunos: 40, cidade: '', estado: '', endereco: '', telefone: '', email: '', cnpj: '', diretor: '', coordenadorAee: '',
  responsavelNome: '', responsavelEmail: '', responsavelTelefone: '',
  gestorNome: '', gestorEmail: '', gestorSenha: '123456'
};

const emptyManagerForm = { schoolId: '', nome: '', email: '', password: '123456' };

function studentsCount(data, schoolId) {
  return (data.students || []).filter((student) => student.schoolId === schoolId).length;
}

function userSchoolIds(user) {
  return [...new Set((user?.schoolIds?.length ? user.schoolIds : [user?.schoolId]).filter(Boolean))];
}

function schoolUsers(data, schoolId) {
  return (data.users || []).filter((user) => userSchoolIds(user).includes(schoolId) && user.role !== 'project_admin');
}

function schoolManagers(data, schoolId) {
  return schoolUsers(data, schoolId).filter((user) => user.role === 'school_admin');
}

function schoolProfessionals(data, schoolId) {
  return schoolUsers(data, schoolId).filter((user) => user.role === 'professional');
}

function schoolParents(data, schoolId) {
  return schoolUsers(data, schoolId).filter((user) => user.role === 'parent');
}

function normalizeSchoolForm(form) {
  return {
    nome: form.nome.trim(),
    logoUrl: '',
    corPrimaria: '#4f46e5',
    limiteAlunos: Number(form.limiteAlunos) || 40,
    cidade: form.cidade.trim(),
    estado: form.estado.trim().toUpperCase(),
    endereco: form.endereco.trim(),
    telefone: form.telefone.trim(),
    email: form.email.trim(),
    cnpj: form.cnpj.trim(),
    diretor: form.diretor.trim(),
    coordenadorAee: form.coordenadorAee.trim(),
    responsavelNome: form.responsavelNome.trim(),
    responsavelEmail: form.responsavelEmail.trim(),
    responsavelTelefone: form.responsavelTelefone.trim()
  };
}

export function ProjectAdmin({ data, setData, onSave }) {
  const schools = data.schools || [];
  const users = data.users || [];
  const professionals = users.filter((item) => item.role === 'professional');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [managerForm, setManagerForm] = useState(emptyManagerForm);
  const [editing, setEditing] = useState(null);

  function persist(next, msg) {
    setData(next);
    onSave(next);
    setMessage(msg);
  }

  function emailExists(email) {
    return users.some((user) => user.email.trim().toLowerCase() === email.trim().toLowerCase());
  }

  function addSchool(e) {
    e.preventDefault();
    if (schools.some((school) => school.nome.trim().toLowerCase() === form.nome.trim().toLowerCase())) {
      setMessage('Já existe escola com este nome.');
      return;
    }
    if (emailExists(form.gestorEmail)) {
      setMessage('Já existe usuário com o e-mail da gestão escolar.');
      return;
    }
    const schoolId = generateId('school');
    const school = { id: schoolId, ...normalizeSchoolForm(form) };
    const manager = { uid: generateId('usr'), schoolId, schoolIds: [schoolId], role: 'school_admin', nome: form.gestorNome.trim(), email: form.gestorEmail.trim(), password: form.gestorSenha || '123456' };
    persist({ ...data, schools: [...schools, school], users: [...users, manager] }, 'Escola criada com gestão escolar inicial.');
    setForm(emptyForm);
    setManagerForm({ ...emptyManagerForm, schoolId });
  }

  function addManager(e) {
    e.preventDefault();
    if (!managerForm.schoolId) {
      setMessage('Selecione uma escola para criar o gestor.');
      return;
    }
    if (!schools.some((school) => school.id === managerForm.schoolId)) {
      setMessage('Escola selecionada não encontrada.');
      return;
    }
    if (emailExists(managerForm.email)) {
      setMessage('Já existe usuário com este e-mail.');
      return;
    }
    const manager = {
      uid: generateId('usr'),
      schoolId: managerForm.schoolId,
      schoolIds: [managerForm.schoolId],
      role: 'school_admin',
      nome: managerForm.nome.trim(),
      email: managerForm.email.trim(),
      password: managerForm.password || '123456'
    };
    const schoolName = schools.find((school) => school.id === managerForm.schoolId)?.nome || 'escola';
    persist({ ...data, users: [...users, manager] }, `Gestor escolar criado para ${schoolName}.`);
    setManagerForm({ ...emptyManagerForm, schoolId: managerForm.schoolId });
  }

  function toggleProfessionalSchool(uid, schoolId) {
    const professional = users.find((item) => item.uid === uid && item.role === 'professional');
    if (!professional) return;
    const current = userSchoolIds(professional);
    const nextSchoolIds = current.includes(schoolId) ? current.filter((id) => id !== schoolId) : [...current, schoolId];
    if (!nextSchoolIds.length) {
      setMessage('O professor deve permanecer vinculado a pelo menos uma escola.');
      return;
    }
    const nextUsers = users.map((item) => item.uid === uid ? { ...item, schoolId: nextSchoolIds[0], schoolIds: nextSchoolIds } : item);
    const schoolNames = nextSchoolIds.map((id) => schools.find((school) => school.id === id)?.nome || id);
    persist({ ...data, users: nextUsers }, `${professional.nome} vinculado a: ${schoolNames.join(', ')}.`);
  }

  function startEdit(school) {
    setEditing({ ...school });
    setMessage('');
  }

  function saveEdit(e) {
    e.preventDefault();
    if (!editing) return;
    const count = studentsCount(data, editing.id);
    const limit = Number(editing.limiteAlunos) || 0;
    if (limit > 0 && limit < count) {
      setMessage(`O limite não pode ser menor que os ${count} alunos já cadastrados.`);
      return;
    }
    const nextSchool = { ...editing, limiteAlunos: limit || 40, estado: String(editing.estado || '').toUpperCase() };
    const next = { ...data, schools: schools.map((school) => school.id === editing.id ? nextSchool : school) };
    persist(next, 'Dados da escola atualizados.');
    setEditing(null);
  }

  return <>
    <header className='header'>
      <div>
        <p>administração do projeto</p>
        <h1>Escolas e acesso institucional</h1>
        <span>Cadastre escolas, defina limites, crie gestores e controle em quais escolas cada professor AEE pode atuar.</span>
      </div>
    </header>

    {message && <p className='hint'>{message}</p>}

    <div className='split'>
      <section className='card'>
        <h2>Nova escola</h2>
        <form className='form' onSubmit={addSchool}>
          <label>Nome da escola<input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></label>
          <div className='inline-grid'>
            <label>CNPJ<input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></label>
            <label>Cidade<input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} /></label>
            <label>UF<input maxLength='2' value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} /></label>
          </div>
          <label>Endereço<input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></label>
          <div className='inline-grid'>
            <label>Telefone<input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></label>
            <label>E-mail institucional<input type='email' value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label>Limite de alunos<input type='number' min='1' required value={form.limiteAlunos} onChange={(e) => setForm({ ...form, limiteAlunos: e.target.value })} /></label>
          </div>
          <label>Direção<input value={form.diretor} onChange={(e) => setForm({ ...form, diretor: e.target.value })} /></label>
          <label>Coordenação AEE<input value={form.coordenadorAee} onChange={(e) => setForm({ ...form, coordenadorAee: e.target.value })} /></label>
          <h3>Responsável institucional da escola</h3>
          <p className='hint'>Contato administrativo da instituição. Não é responsável familiar de aluno.</p>
          <label>Nome<input value={form.responsavelNome} onChange={(e) => setForm({ ...form, responsavelNome: e.target.value })} /></label>
          <div className='inline-grid'>
            <label>E-mail<input type='email' value={form.responsavelEmail} onChange={(e) => setForm({ ...form, responsavelEmail: e.target.value })} /></label>
            <label>Telefone<input value={form.responsavelTelefone} onChange={(e) => setForm({ ...form, responsavelTelefone: e.target.value })} /></label>
            <span />
          </div>
          <h3>Gestão escolar inicial</h3>
          <label>Nome<input required value={form.gestorNome} onChange={(e) => setForm({ ...form, gestorNome: e.target.value })} /></label>
          <label>E-mail<input required type='email' value={form.gestorEmail} onChange={(e) => setForm({ ...form, gestorEmail: e.target.value })} /></label>
          <label>Senha inicial<input required value={form.gestorSenha} onChange={(e) => setForm({ ...form, gestorSenha: e.target.value })} /></label>
          <button>Criar escola</button>
        </form>
      </section>

      <section className='card'>
        <h2>Criar gestor para escola existente</h2>
        <p className='hint'>Use quando uma escola já cadastrada precisar de outro usuário de gestão. O novo gestor terá acesso somente à escola selecionada.</p>
        <form className='form' onSubmit={addManager}>
          <label>Escola
            <select required value={managerForm.schoolId} onChange={(e) => setManagerForm({ ...managerForm, schoolId: e.target.value })}>
              <option value=''>Selecione</option>
              {schools.map((school) => <option key={school.id} value={school.id}>{school.nome}</option>)}
            </select>
          </label>
          <label>Nome do gestor<input required value={managerForm.nome} onChange={(e) => setManagerForm({ ...managerForm, nome: e.target.value })} /></label>
          <label>E-mail<input required type='email' value={managerForm.email} onChange={(e) => setManagerForm({ ...managerForm, email: e.target.value })} /></label>
          <label>Senha inicial<input required value={managerForm.password} onChange={(e) => setManagerForm({ ...managerForm, password: e.target.value })} /></label>
          <button disabled={!schools.length}>Criar gestor escolar</button>
        </form>
      </section>
    </div>

    <section className='card'>
      <h2>Vínculos dos professores com escolas</h2>
      <p className='hint'>Marque todas as escolas em que o professor atua. Quando houver dois ou mais vínculos, o professor poderá escolher o vínculo ativo no menu lateral.</p>
      <div className='list'>
        {professionals.length === 0 && <p>Nenhum professor AEE cadastrado.</p>}
        {professionals.map((professional) => {
          const linkedIds = userSchoolIds(professional);
          return <article key={professional.uid}>
            <h3>{professional.nome}</h3>
            <small>{professional.email}</small>
            <div className='checkbox-list'>
              {schools.map((school) => <label key={school.id} className='checkbox-item'><input type='checkbox' checked={linkedIds.includes(school.id)} onChange={() => toggleProfessionalSchool(professional.uid, school.id)} /> {school.nome}</label>)}
            </div>
          </article>;
        })}
      </div>
    </section>

    <section className='card'>
      <h2>Escolas cadastradas</h2>
      <div className='list'>
        {schools.map((school) => {
          const managers = schoolManagers(data, school.id);
          return <article key={school.id}>
            <h3>{school.nome}</h3>
            <small>{school.cidade || '-'} {school.estado || ''} · {school.email || 'sem e-mail institucional'}</small>
            <p><b>Alunos:</b> {studentsCount(data, school.id)} / {school.limiteAlunos || 0}</p>
            <p><b>Gestão escolar:</b> {managers.length ? managers.map((user) => `${user.nome} (${user.email})`).join(', ') : '-'}</p>
            <p><b>Profissionais AEE:</b> {schoolProfessionals(data, school.id).length}</p>
            <p><b>Responsáveis familiares cadastrados:</b> {schoolParents(data, school.id).length}</p>
            <p><b>Responsável institucional:</b> {school.responsavelNome || '-'} {school.responsavelEmail ? `· ${school.responsavelEmail}` : ''}</p>
            <button type='button' onClick={() => startEdit(school)}>Editar dados da escola</button>
          </article>;
        })}
      </div>
    </section>

    {editing && <section className='card'>
      <h2>Editar escola</h2>
      <form className='form' onSubmit={saveEdit}>
        <label>Nome da escola<input required value={editing.nome || ''} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} /></label>
        <div className='inline-grid'>
          <label>CNPJ<input value={editing.cnpj || ''} onChange={(e) => setEditing({ ...editing, cnpj: e.target.value })} /></label>
          <label>Cidade<input value={editing.cidade || ''} onChange={(e) => setEditing({ ...editing, cidade: e.target.value })} /></label>
          <label>UF<input maxLength='2' value={editing.estado || ''} onChange={(e) => setEditing({ ...editing, estado: e.target.value })} /></label>
        </div>
        <label>Endereço<input value={editing.endereco || ''} onChange={(e) => setEditing({ ...editing, endereco: e.target.value })} /></label>
        <div className='inline-grid'>
          <label>Telefone<input value={editing.telefone || ''} onChange={(e) => setEditing({ ...editing, telefone: e.target.value })} /></label>
          <label>E-mail institucional<input type='email' value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
          <label>Limite de alunos<input type='number' min='1' value={editing.limiteAlunos || 1} onChange={(e) => setEditing({ ...editing, limiteAlunos: e.target.value })} /></label>
        </div>
        <label>Direção<input value={editing.diretor || ''} onChange={(e) => setEditing({ ...editing, diretor: e.target.value })} /></label>
        <label>Coordenação AEE<input value={editing.coordenadorAee || ''} onChange={(e) => setEditing({ ...editing, coordenadorAee: e.target.value })} /></label>
        <h3>Responsável institucional</h3>
        <label>Nome<input value={editing.responsavelNome || ''} onChange={(e) => setEditing({ ...editing, responsavelNome: e.target.value })} /></label>
        <div className='inline-grid'>
          <label>E-mail<input type='email' value={editing.responsavelEmail || ''} onChange={(e) => setEditing({ ...editing, responsavelEmail: e.target.value })} /></label>
          <label>Telefone<input value={editing.responsavelTelefone || ''} onChange={(e) => setEditing({ ...editing, responsavelTelefone: e.target.value })} /></label>
          <span />
        </div>
        <div className='actions'>
          <button>Salvar alterações</button>
          <button type='button' onClick={() => setEditing(null)}>Cancelar</button>
        </div>
      </form>
    </section>}
  </>;
}
